import { useCallback, useEffect, useState } from "react";
import { commitPaywallExitReason, readPaywallOffer, type PaywallExitReason, type PaywallOfferState } from "./paywallOffer";

type PaywallRoute = "main" | "question" | PaywallExitReason;

function readRoute(): PaywallRoute | null {
  const value = new URL(window.location.href).searchParams.get("paywall");
  return value === "main" || value === "question" || value === "price" ||
    value === "usage" || value === "information" ? value : null;
}

function writeRoute(route: PaywallRoute) {
  const url = new URL(window.location.href);
  url.searchParams.set("paywall", route);
  url.hash = "founding_member";
  window.history.replaceState(window.history.state, "", url);
}

export function usePaywallOffer(scope: string, fromRecoveryLink: boolean) {
  const storageKey = `parables:paywall-offer:v1:${scope}`;
  const [questionOpen, setQuestionOpen] = useState(() => readRoute() === "question");
  const [state, setState] = useState<PaywallOfferState | null>(() => {
    let saved: PaywallOfferState | null = null;
    try {
      saved = readPaywallOffer(localStorage.getItem(storageKey));
    } catch {
      /* Storage may be unavailable. */
    }
    const route = readRoute();
    if (route === "main" || route === "question")
      return saved ? { ...saved, visible: false } : null;
    if (route) return { visible: true, reason: route };
    return saved ?? (fromRecoveryLink ? { visible: true, reason: "price" } : null);
  });

  useEffect(() => {
    const syncRoute = () => {
      const route = readRoute();
      setQuestionOpen(route === "question");
      if (route === "main" || route === "question") {
        setState(previous => previous ? { ...previous, visible: false } : null);
      } else if (route) {
        setState({ visible: true, reason: route });
      }
    };
    window.addEventListener("popstate", syncRoute);
    window.addEventListener("hashchange", syncRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("hashchange", syncRoute);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      /* Keep working in memory. */
    }
  }, [state, storageKey]);

  // Keep offer visibility in sync across open copies of this funnel.
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === storageKey && !readRoute()) setState(readPaywallOffer(event.newValue));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [storageKey]);

  const reveal = useCallback(() => {
    // Returning to pricing finishes the information branch. Never reopen it.
    if (state?.reason === "information") return;
    if (state?.reason) {
      writeRoute(state.reason);
      setState(previous => previous ? { ...previous, visible: true } : previous);
    } else {
      writeRoute("question");
      setQuestionOpen(true);
    }
  }, [state?.reason]);
  const commitReason = useCallback((reason: PaywallExitReason) => {
    let saved: PaywallOfferState | null = null;
    try { saved = readPaywallOffer(localStorage.getItem(storageKey)); } catch { /* Keep working in memory. */ }
    const next = commitPaywallExitReason(saved ?? state, reason);
    // Persist at confirmation so an immediate reload cannot reopen the question.
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* Keep working in memory. */ }
    writeRoute(reason);
    setState(next);
    setQuestionOpen(false);
  }, [state, storageKey]);
  const dismiss = useCallback(
    () => {
      writeRoute("main");
      setQuestionOpen(false);
      setState((previous) =>
        previous ? { ...previous, visible: false } : previous,
      );
    },
    [],
  );
  return {
    visible: state?.visible ?? false,
    reason: state?.reason ?? "price",
    questionOpen,
    commitReason,
    reveal,
    dismiss,
  };
}
